import React, { type ComponentProps, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, ModalBase, notify, Spinner } from '@bynder/design-system';
import { type AssetPickerOptions, GenAIModal, OriginalAsset } from '@bynder/image-gen-frontend-lib';
import { useTranslate } from '@bynder/localization';
import { getBynderAccessToken, getPortalDomain } from 'packages/store/platform/platform.selectors';
import { getSplitFeatures } from 'packages/store/user/user.selectors';
import { ActiveSplits } from 'packages/store/user/types';
import AssetsPicker from 'packages/pages/components/AssetsPickerModal/components/AssetsPicker';
import Sidebar from 'packages/pages/components/AssetsPickerModal/components/AssetsPickerSidebar';
import UploadButton from 'packages/pages/components/Uploader/UploadButton';
import useActions from 'packages/pages/components/AssetsPickerModal/components/useActions';
import { StyledModalBasedContent } from 'packages/pages/components/AssetsPickerModal/components/index.styled';
import { AssetGroup } from 'packages/pages/editor/ContentPickerModal/types';
import assetsService from 'packages/services/assetsService';
import { isAllowedImageFormat, isAllowedReferenceFormat, DOC_DOCX_EXTENSIONS, processPickerAssets } from './utils';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSave: ({ enhancementId, fileId }: { enhancementId: number; fileId: string }) => Promise<void>;
    sessionId: string;
    originalAsset: OriginalAsset;
    saveButtonLabel?: string;
}

function ImageGen({ isOpen, onClose, onSave, sessionId, originalAsset, saveButtonLabel }: Props) {
    const { translate } = useTranslate();
    const token = useSelector(getBynderAccessToken);
    const portalDomain = useSelector(getPortalDomain);
    const splitFeatures = useSelector(getSplitFeatures);
    const isAsyncEnhancementsEnabled = splitFeatures?.[ActiveSplits.imageGenAsyncProcessing] === 'on';
    type ShowAssetPicker = NonNullable<ComponentProps<typeof GenAIModal>['showAssetPicker']>;
    type AddAssetsCallback = Parameters<ShowAssetPicker>[0];
    type AssetPickerPayload = Parameters<AddAssetsCallback>[0];

    const [addAssetsCallback, setAddAssetsCallback] = useState<AddAssetsCallback | undefined>(undefined);
    const [assetPickerOptions, setAssetPickerOptions] = useState<AssetPickerOptions>({});
    const [isLocalAssetLoading, setIsLocalAssetLoading] = useState(false);

    const {
        assetType,
        wrapperRef,
        collectionId,
        selectedLocalAssets,
        handleDamClick,
        setSelectedCollection,
        onLocalAssetClick,
    } = useActions({ allowMultiSelect: true, type: 'image' });

    const getJWT = useCallback(async () => {
        const testToken = localStorage.getItem('testGenAIToken');

        if (testToken) {
            return testToken;
        }

        return token;
    }, [token]);

    const hostURL = useMemo(() => {
        const testHostURL = localStorage.getItem('testGenAIHostURL');

        if (testHostURL) {
            return testHostURL;
        }

        // with trim last slash if exists
        return `https://${portalDomain}`;
    }, [portalDomain]);

    const handleAssetPickerClose = useCallback(() => {
        setAddAssetsCallback(undefined);
        setAssetPickerOptions({});
    }, []);

    const showAssetPicker = useCallback((callback: AddAssetsCallback, options?: AssetPickerOptions) => {
        setAddAssetsCallback(() => callback);
        setAssetPickerOptions(options ?? {});
    }, []);

    const closeAssetPicker = useCallback(() => {
        setAddAssetsCallback(undefined);
        setAssetPickerOptions({});
    }, []);

    const handleAssetPickerSelect = useCallback(
        (assets: any, metadata?: Record<string, any>) => {
            if (!addAssetsCallback) {
                handleAssetPickerClose();

                return;
            }

            const { payload, invalidCount, docDocxCount } = processPickerAssets(assets, metadata, assetPickerOptions);

            if (docDocxCount > 0) {
                notify({
                    title: translate('image-gen.error.docDocxNotSupported'),
                    variant: 'error',
                });
            } else if (invalidCount > 0) {
                notify({
                    title: translate('modal.assetsPicker.error.title.invalidFileType'),
                    variant: 'error',
                });
            }

            if (payload.length) {
                addAssetsCallback(payload);
            }

            handleAssetPickerClose();
        },
        [addAssetsCallback, handleAssetPickerClose, translate, assetPickerOptions],
    );

    const assetPickerCompactViewProps = useMemo(
        () => ({
            language: 'en_US',
            mode: (assetPickerOptions.maxAssets ?? 1) > 1 ? 'MultiSelect' : 'SingleSelect',
            assetTypes: assetPickerOptions.allowDocuments ? ['IMAGE', 'DOCUMENT'] : ['IMAGE'],
            isContainerMode: true,
            hideSwitch: true,
            selectedAssets: [],
            onSuccess: handleAssetPickerSelect,
        }),
        [handleAssetPickerSelect, assetPickerOptions.maxAssets, assetPickerOptions.allowDocuments],
    );

    const handleLocalAssetConfirm = useCallback(async () => {
        if (!addAssetsCallback || !selectedLocalAssets.length) {
            return;
        }

        const validateFormat = assetPickerOptions.allowDocuments ? isAllowedReferenceFormat : isAllowedImageFormat;

        // Filter out DOC/DOCX files
        const docDocxAssets = selectedLocalAssets.filter((asset) => {
            const ext = (asset.extension || '').toLowerCase();

            return DOC_DOCX_EXTENSIONS.includes(ext as (typeof DOC_DOCX_EXTENSIONS)[number]);
        });
        const allowedAssets = selectedLocalAssets.filter((asset) => {
            const ext = (asset.extension || '').toLowerCase();

            return !DOC_DOCX_EXTENSIONS.includes(ext as (typeof DOC_DOCX_EXTENSIONS)[number]);
        });

        if (docDocxAssets.length > 0) {
            notify({
                title: translate('image-gen.error.docDocxNotSupported'),
                variant: 'error',
            });
        }

        if (!allowedAssets.length) {
            handleAssetPickerClose();

            return;
        }

        const isFormatCompatible = allowedAssets.map(validateFormat).every(Boolean);

        if (!isFormatCompatible) {
            notify({
                title: translate('modal.assetsPicker.error.title.invalidFileType'),
                variant: 'error',
            });

            return;
        }

        setIsLocalAssetLoading(true);

        try {
            const payloadItems = await Promise.all(
                allowedAssets.map(async (asset) => {
                    const { aiEditData } = await assetsService.prepareAIEdit(asset.id);
                    const thumbnail = asset.thumbnails?.['320x320'] || Object.values(asset.thumbnails ?? {})[0] || '';

                    return {
                        fileId: aiEditData.fileId,
                        thumbnail: thumbnail || undefined,
                    };
                }),
            );

            const payload: AssetPickerPayload = payloadItems;

            addAssetsCallback(payload);
            handleAssetPickerClose();
        } catch {
            notify({
                title: translate('modal.assetsPicker.error.title.generic'),
                variant: 'error',
            });
        } finally {
            setIsLocalAssetLoading(false);
        }
    }, [addAssetsCallback, selectedLocalAssets, handleAssetPickerClose, translate, assetPickerOptions]);

    const isAssetPickerOpen = Boolean(addAssetsCallback);

    return (
        <>
            <GenAIModal
                getJWT={getJWT}
                originalAsset={originalAsset}
                sessionId={sessionId}
                isOpen={isOpen}
                onClose={onClose}
                onSave={onSave}
                allowedTools={['REMOVE_BACKGROUND', 'REPLACE_BACKGROUND', 'RESIZE', 'SMART_EDIT']}
                saveButtonLabel={saveButtonLabel || translate('image-gen.modal.apply-changes')}
                isDownloadAvailable
                hostURL={hostURL}
                clientName="STUDIO"
                showAssetPicker={showAssetPicker}
                closeAssetPicker={closeAssetPicker}
                useAsyncEnhancements={isAsyncEnhancementsEnabled}
            />

            <ModalBase
                key={`asset-picker-${originalAsset?.fileId}-${isOpen}`}
                size="fullscreen"
                isOpen={isAssetPickerOpen}
                onClose={handleAssetPickerClose}
            >
                {isAssetPickerOpen && (
                    <>
                        <ModalBase.Header
                            title={translate('modal.assetsPicker.button.add_asset')}
                            additionalActions={
                                assetType === AssetGroup.LOCAL ? (
                                    <UploadButton
                                        iconType="upload"
                                        assetTypes={
                                            assetPickerOptions.allowDocuments ? ['image', 'document'] : ['image']
                                        }
                                        collectionId={collectionId}
                                        multiple
                                    />
                                ) : undefined
                            }
                        />
                        <ModalBase.Sidebar>
                            <Sidebar
                                assetType={assetType}
                                type="image"
                                selectedCollectionId={collectionId}
                                setSelectedCollection={setSelectedCollection}
                                handleDamClick={handleDamClick}
                            />
                        </ModalBase.Sidebar>
                        <ModalBase.Content>
                            <StyledModalBasedContent isLocal={assetType === AssetGroup.LOCAL} ref={wrapperRef}>
                                <AssetsPicker
                                    type="image"
                                    collectionId={collectionId}
                                    assetType={assetType}
                                    selectedItems={selectedLocalAssets}
                                    onSelectLocal={onLocalAssetClick}
                                    compactViewProps={assetPickerCompactViewProps}
                                    isPersonalUploadsAllowed
                                    filterTypes={['IMAGE']}
                                />
                                {isLocalAssetLoading && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                        }}
                                    >
                                        <Spinner />
                                    </div>
                                )}
                            </StyledModalBasedContent>
                        </ModalBase.Content>
                        {assetType === AssetGroup.LOCAL && selectedLocalAssets?.length > 0 && (
                            <ModalBase.Footer
                                actionPrimary={null}
                                actionSecondary={
                                    <Button
                                        variant="primary"
                                        isLoading={isLocalAssetLoading}
                                        onClick={handleLocalAssetConfirm}
                                    >
                                        {translate('modal.assetsPicker.button.add_asset')}
                                    </Button>
                                }
                            />
                        )}
                    </>
                )}
            </ModalBase>
        </>
    );
}

export default React.memo(ImageGen);
